/**
 * A length of yarn, drawn.
 *
 * The old page put a flat rectangle of colour next to each yarn, or rather it put
 * nothing at all, and a yarn shop where you cannot see the yarn is not a yarn shop.
 * This draws the strand: plies twisting along it, a lit crown on each ply, a shaded
 * underside, and a fibre texture lit by a distant lamp so a wool reads as wool
 * rather than as a coloured tube.
 *
 * ### Where each part of the drawing comes from
 *
 * Nothing here is tuned per yarn by hand. The thickness is the stitch gauge
 * inverted, the pitch of the twist is the ply count off the shelf, the fuzz and the
 * sheen are read out of the fibre content string, and the noise seed is a hash of
 * the id so a card looks the same on every load. All of that arithmetic is in
 * `geometry.ts` and `shop.ts`, which is why this file is mostly a list of layers.
 *
 * ### The colours are not in this file
 *
 * A card carries `data-dye`, `tokens.css` turns that into four custom properties,
 * and every fill and stroke below reads one of them through a class. So a colourway
 * is data plus a stylesheet, there is no colour literal and no style object in the
 * component, and `styles/house.test.ts` keeps it that way.
 *
 * ### Layer order, since it is the whole trick
 *
 * Under the strand: a blurred copy of it, wider, which is the halo standing off the
 * edge. Inside a mask cut to the strand: the dye, a lit side, a shaded side, the
 * grooves between plies, the crown of each ply, a specular hairline, and the fibre
 * texture last so it sits over everything it is meant to be a texture of.
 */

import {
  STRAND_BOX,
  STRAND_SPINE,
  smoothPath,
  strandWidth,
  textureSeed,
  twist,
} from './geometry';
import { fibreCharacter, shelfEntry, type FibreCharacter } from '../shop';
import type { YarnRecord } from '../catalogue';

/**
 * How each fibre reading is drawn.
 *
 * `noise` is the turbulence frequency, anisotropic on purpose: fibre runs along the
 * strand, so the noise has to be finer across it than along it or the texture reads
 * as gravel. `halo` is how far the fuzz stands off the edge, in strand widths.
 * `relief` is how deep the lighting pushes the texture, and `spec` is how hard the
 * specular hairline lands, which is the difference between an alpaca and something
 * with a nylon in it.
 */
const HANDLE: Readonly<Record<FibreCharacter, {
  readonly noise: string;
  readonly halo: number;
  readonly relief: number;
  readonly spec: number;
}>> = Object.freeze({
  halo: { noise: '1.35 0.42', halo: 0.34, relief: 2.6, spec: 0.16 },
  sheen: { noise: '0.45 0.14', halo: 0.1, relief: 1.1, spec: 0.62 },
  crisp: { noise: '0.85 0.26', halo: 0.16, relief: 1.9, spec: 0.3 },
});

/** A vertical nudge, which is how the lit and shaded sides follow the strand. */
const shiftDown = (amount: number): string =>
  `translate(0 ${Math.round(amount * 100) / 100})`;

export interface SkeinProps {
  readonly yarn: YarnRecord;
}

export const Skein = ({ yarn }: SkeinProps): JSX.Element => {
  const entry = shelfEntry(yarn.id);
  const character = fibreCharacter(yarn.fibre);
  const handle = HANDLE[character];

  const spine = smoothPath(STRAND_SPINE);
  const width = strandWidth(yarn.stsPer4in);
  const ply = twist(width, entry.plies);

  // Two ids per card, and they have to be unique across a page with seven of these
  // on it, so they are keyed on the yarn rather than on a counter.
  const fibreId = `fibre-${yarn.id}`;
  const bandId = `band-${yarn.id}`;

  const plyWord = entry.plies === 1 ? 'single' : `${entry.plies} ply`;

  return (
    <svg
      aria-label={`A length of ${yarn.name} in ${entry.colourway}, ${plyWord}, drawn at ${yarn.weight} weight.`}
      className="skein"
      data-handle={character}
      role="img"
      viewBox={`0 0 ${STRAND_BOX.width} ${STRAND_BOX.height}`}
    >
      <defs>
        {/*
          The mask is the strand itself, stroked. A clip path could not do this: a
          clip takes a fill region and the strand is a stroke, so masking is the
          only way to pour the layers into the shape of a line.
        */}
        <mask id={bandId} maskUnits="userSpaceOnUse">
          <path
            className="skein__cut"
            d={spine}
            strokeLinecap="round"
            strokeWidth={width}
          />
        </mask>

        <filter id={fibreId} x="-6%" y="-20%" width="112%" height="140%">
          <feTurbulence
            baseFrequency={handle.noise}
            numOctaves={3}
            result="fibres"
            seed={textureSeed(yarn.id)}
            type="fractalNoise"
          />
          {/*
            The noise is a height field, not a picture, and this is the step that
            turns it into one: a distant lamp up and to the left, which is where the
            light comes from everywhere else on this page.
          */}
          <feDiffuseLighting
            className="skein__lamp"
            diffuseConstant={1.1}
            in="fibres"
            surfaceScale={handle.relief}
          >
            <feDistantLight azimuth={228} elevation={56} />
          </feDiffuseLighting>
        </filter>
      </defs>

      {/* The halo, standing off the edge of the strand. Drawn first, so it is under
          everything, and blurred in the stylesheet rather than by a second filter. */}
      <path
        className="skein__halo"
        d={spine}
        strokeLinecap="round"
        strokeWidth={width * (1 + handle.halo)}
      />

      <g mask={`url(#${bandId})`}>
        <rect className="skein__dye" height="100%" width="100%" x="0" y="0" />

        <path
          className="skein__lit"
          d={spine}
          strokeLinecap="round"
          strokeWidth={width * 0.6}
          transform={shiftDown(-width * 0.17)}
        />
        <path
          className="skein__shade"
          d={spine}
          strokeLinecap="round"
          strokeWidth={width * 0.46}
          transform={shiftDown(width * 0.27)}
        />

        <path className="skein__grooves" d={ply.grooves} strokeWidth={ply.grooveWidth} />
        <path className="skein__crowns" d={ply.sheens} strokeWidth={ply.sheenWidth} />

        <path
          className="skein__spec"
          d={spine}
          opacity={handle.spec}
          strokeLinecap="round"
          strokeWidth={width * 0.13}
          transform={shiftDown(-width * 0.27)}
        />

        <rect
          className="skein__fibres"
          filter={`url(#${fibreId})`}
          height="100%"
          width="100%"
          x="0"
          y="0"
        />
      </g>
    </svg>
  );
};
